import type { Task } from "@app/core";
import { addDays, fromIso, startOfDay } from "@app/core";

import { isOverdue } from "../agenda/agenda.js";

/** Pure "Plan my day" engine: pick the queue items that could use a concrete
 *  time on a chosen day and first-fit them into the free gaps between the
 *  day's existing timed blocks. No IO, time comes in as `now` (rule 8). */

export interface PlanSlot {
  task: Task;
  /** Proposed local start. */
  start: Date;
  minutes: number;
}

export interface DayPlan {
  /** Proposed slots, ordered by start time. */
  slots: PlanSlot[];
  /** Candidates that did not fit, paired with the same effective estimate the
   * planner tested (important for all-day/zero-duration work). */
  unfit: PlanUnfit[];
}

export interface PlanUnfit {
  task: Task;
  minutes: number;
}

/** Fallback length for a candidate without a duration estimate. */
export const PLAN_FALLBACK_MINUTES = 30;
/** Proposed starts snap up to this grid so slots read as tidy times. */
const SNAP_MINUTES = 5;

function sameLocalDay(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

/**
 * Which queue tasks are eligible to receive a time on `day`: open, movable
 * (not recurring/virtual/an event — occurrence moves belong to the calendar),
 * and either undated, overdue, or all-day on that very day. Timed tasks
 * already on the day are part of the schedule; timed tasks on OTHER days are
 * never moved implicitly. Order is preserved (pass the decision-ordered queue).
 */
export function planCandidates(queue: ReadonlyArray<Task>, day: Date, now: Date): Task[] {
  return queue.filter((t) => {
    if (t.status !== "open" || t.deleted_at) return false;
    if (t.is_event || t.recurrence_id || t.virtual) return false;
    if (!t.due_at) return true;
    const due = fromIso(t.due_at);
    if (t.all_day && sameLocalDay(due, day)) return true;
    return isOverdue(t, now);
  });
}

interface Interval { start: number; end: number; }

function snapUp(ms: number): number {
  const step = SNAP_MINUTES * 60_000;
  return Math.ceil(ms / step) * step;
}

/** Sorted, merged busy intervals intersecting the selected local day. */
function busyIntervals(busy: ReadonlyArray<Task>, day: Date): Interval[] {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = startOfDay(addDays(day, 1)).getTime();
  const raw = busy
    // Snooze defers the reminder, not the calendar reservation: its dimmed
    // block remains on the calendar and must still prevent double-booking.
    .filter((t) => t.status !== "done" && t.due_at && !t.all_day)
    .map((t) => {
      const start = fromIso(t.due_at!).getTime();
      const end = start + Math.max(0, t.duration_minutes) * 60_000;
      return { start: Math.max(start, dayStart), end: Math.min(end, dayEnd) };
    })
    .filter((iv) => iv.end > iv.start)
    .sort((a, b) => a.start - b.start);
  const merged: Interval[] = [];
  for (const iv of raw) {
    const last = merged[merged.length - 1];
    if (last && iv.start <= last.end) last.end = Math.max(last.end, iv.end);
    else merged.push({ ...iv });
  }
  return merged;
}

/**
 * First-fit the candidates (in the given order) into the free gaps of
 * `day` between `startHour` and `endHour`, around the existing `busy` blocks.
 * When `day` is today the window starts no earlier than `now`. Candidates the
 * remaining free time cannot hold are returned as `unfit`.
 */
export function planDay(
  candidates: ReadonlyArray<Task>,
  busy: ReadonlyArray<Task>,
  day: Date,
  opts: { startHour: number; endHour: number; fallbackMinutes?: number },
  now: Date,
): DayPlan {
  // These are local WALL-CLOCK anchors, not elapsed hours after midnight. The
  // distinction matters on 23/25-hour DST transition days.
  const startAnchor = startOfDay(day);
  startAnchor.setHours(opts.startHour, 0, 0, 0);
  const endAnchor = startOfDay(day);
  endAnchor.setHours(opts.endHour, 0, 0, 0);
  let windowStart = startAnchor.getTime();
  const windowEnd = endAnchor.getTime();
  if (sameLocalDay(day, now)) windowStart = Math.max(windowStart, snapUp(now.getTime()));

  const intervals = busyIntervals(busy, day);
  const slots: PlanSlot[] = [];
  const unfit: PlanUnfit[] = [];

  for (const task of candidates) {
    // An all-day duration describes logical occupied days (normally 1440), not
    // a useful time estimate for turning that item into a clock-time block.
    // Plan it with the configured fallback and persist that slot length when
    // applying the plan.
    const fallback = opts.fallbackMinutes ?? PLAN_FALLBACK_MINUTES;
    const minutes = !task.all_day && task.duration_minutes > 0 ? task.duration_minutes : fallback;
    const need = minutes * 60_000;
    let t = windowStart;
    let placedAt: number | null = null;
    for (const iv of intervals) {
      if (iv.end <= t) continue;
      if (t + need <= iv.start) break;
      t = snapUp(Math.max(t, iv.end));
    }
    if (t + need <= windowEnd) placedAt = t;

    if (placedAt === null) {
      unfit.push({ task, minutes });
      continue;
    }
    const iv = { start: placedAt, end: placedAt + need };
    const at = intervals.findIndex((x) => x.start > iv.start);
    intervals.splice(at === -1 ? intervals.length : at, 0, iv);
    slots.push({ task, start: new Date(placedAt), minutes });
  }

  slots.sort((a, b) => a.start.getTime() - b.start.getTime());
  return { slots, unfit };
}
